/**
 * Orchestration Stream Client
 *
 * Handles SSE (Server-Sent Events) streaming for real-time orchestration progress.
 * Connects to the backend's SSE endpoint and emits typed events.
 */

import type {
  AgentType,
  Citation,
  OrchestrationEvent,
  StepIcon,
  ToolUseSource,
} from '../types';

// ======== Types ========

export interface StreamOptions {
  onEvent: (event: OrchestrationEvent) => void;
  onError: (error: Error) => void;
  onComplete: () => void;
}

export interface SSEMessage {
  type: string;
  data: unknown;
}

// ======== Stream Client ========

export class OrchestrationStreamClient {
  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 3;
  private readonly reconnectDelay = 1000;
  /**
   * Watchdog: if no SSE event (of any type) arrives within this window
   * the stream is considered stalled. EventSource has no inactivity
   * timeout of its own, so a backend that connects but never emits — or
   * hangs mid-run — would otherwise leave the UI showing "Synthesizing…"
   * forever. We surface a recoverable error instead so the caller can
   * offer a retry.
   */
  private readonly inactivityTimeoutMs = 30000;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private streamOptions: StreamOptions | null = null;

  constructor(
    private readonly baseUrl: string = '/api/copilot/orchestration'
  ) {}

  /**
   * Subscribe to orchestration progress via SSE
   *
   * @param sessionId - The session ID to subscribe to
   * @param planId - The plan ID to subscribe to (optional)
   * @param options - Event handlers
   * @returns Unsubscribe function
   */
  subscribe(
    sessionId: string,
    planId: string | undefined,
    options: StreamOptions
  ): () => void {
    const url = planId
      ? `${this.baseUrl}/${sessionId}/stream?planId=${planId}`
      : `${this.baseUrl}/${sessionId}/stream`;

    this.connect(url, options);

    return () => this.disconnect();
  }

  /**
   * Connect to the SSE endpoint
   */
  private connect(url: string, options: StreamOptions): void {
    this.disconnect(); // Clean up any existing connection

    this.streamOptions = options;
    this.eventSource = new EventSource(url, { withCredentials: true });
    this.resetInactivityTimer();

    // Define all event types the backend can emit
    const eventTypes = [
      'connected',
      'mode-detected',
      'plan-start',
      'plan-ready',
      'agent-started',
      'agent-progress',
      'agent-complete',
      'step-start', // Thinking state UI
      'step-progress', // Thinking state UI
      'step-complete', // Thinking state UI
      'step-error', // Thinking state UI
      'text-delta', // Real-time text streaming (Claude Code style)
      'citation',
      'synthesis-start',
      'synthesis-progress',
      'complete',
      'error',
      'cancelled',
      'stream-complete',
    ];

    // Add listeners for each event type
    eventTypes.forEach(type => {
      this.eventSource!.addEventListener(type, (event: Event) => {
        // Any inbound event means the stream is alive — re-arm the watchdog.
        this.resetInactivityTimer();
        const messageEvent = event as MessageEvent;
        try {
          const data = JSON.parse(messageEvent.data);
          const transformedEvent = this.transformEvent(type, data);
          if (transformedEvent) {
            options.onEvent(transformedEvent);

            // Handle terminal events
            if (type === 'complete' || type === 'stream-complete') {
              options.onComplete();
              this.disconnect();
            } else if (type === 'error') {
              // Don't disconnect on error - let the caller decide
            } else if (type === 'cancelled') {
              options.onComplete();
              this.disconnect();
            }
          }
        } catch (e) {
          console.error(`Failed to parse SSE event (${type}):`, e);
        }
      });
    });

    // Handle connection errors
    this.eventSource.onerror = (_error: Event) => {
      console.error('SSE connection error');

      // Attempt reconnection
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => {
          console.log(
            `Reconnecting... attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`
          );
          this.connect(url, options);
        }, this.reconnectDelay * this.reconnectAttempts);
      } else {
        options.onError(
          new Error('SSE connection failed after multiple attempts')
        );
        this.disconnect();
      }
    };

    // Handle successful connection
    this.eventSource.onopen = () => {
      this.reconnectAttempts = 0; // Reset on successful connection
    };
  }

  /**
   * Disconnect from the SSE endpoint
   */
  disconnect(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * (Re)arm the inactivity watchdog. If no SSE event arrives within
   * `inactivityTimeoutMs`, surface a recoverable error through the
   * caller's `onEvent` handler (so the existing error/retry UI can take
   * over) and tear the connection down without auto-reconnecting.
   */
  private resetInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    this.inactivityTimer = setTimeout(() => {
      this.inactivityTimer = null;
      this.streamOptions?.onEvent({
        type: 'error',
        error: 'Stream stalled — no activity for 30s. Please retry.',
        recoverable: true,
      });
      this.disconnect();
    }, this.inactivityTimeoutMs);
  }

  /**
   * Check if the client is connected
   */
  isConnected(): boolean {
    return (
      this.eventSource !== null &&
      this.eventSource.readyState === EventSource.OPEN
    );
  }

  /**
   * Transform raw SSE event data into typed OrchestrationEvent
   */
  private transformEvent(
    type: string,
    data: Record<string, unknown>
  ): OrchestrationEvent | null {
    switch (type) {
      case 'connected':
        // Connection confirmed - emit as plan-start if we have plan info
        if (data.planId) {
          return {
            type: 'plan-start',
            planId: data.planId as string,
            query: (data.query as string) || '',
            mode:
              (data.mode as 'auto' | 'explore' | 'analyze' | 'deep-research') ||
              'auto',
          };
        }
        return null;

      case 'mode-detected':
        // Mode was auto-detected - embed in plan-start
        return {
          type: 'plan-start',
          planId: (data.planId as string) || '',
          query: (data.query as string) || '',
          mode: data.mode as 'auto' | 'explore' | 'analyze' | 'deep-research',
        };

      case 'plan-start':
        return {
          type: 'plan-start',
          planId: data.planId as string,
          query: data.query as string,
          mode: data.mode as 'auto' | 'explore' | 'analyze' | 'deep-research',
        };

      case 'plan-ready':
        return {
          type: 'plan-ready',
          planId: data.planId as string,
          steps: data.steps as OrchestrationEvent extends { type: 'plan-ready' }
            ? OrchestrationEvent['steps']
            : never,
          estimatedTime: (data.estimatedTime as number) || 0,
        };

      case 'agent-started':
        return {
          type: 'agent-event',
          event: {
            type: 'started',
            agentType: data.agentType as AgentType,
            task: data.task as string,
          },
        };

      case 'agent-progress':
        return {
          type: 'agent-event',
          event: {
            type: 'progress',
            agentType: data.agentType as AgentType,
            message: data.message as string,
            percentage: data.percentage as number | undefined,
          },
        };

      case 'agent-complete':
        return {
          type: 'agent-event',
          event: {
            type: 'complete',
            agentType: data.agentType as AgentType,
            result: {
              response: data.response as string | undefined,
              citations: data.citations as Citation[] | undefined,
            },
          },
        };

      case 'citation':
        return {
          type: 'citation',
          citation: (data.citation as Citation) || {
            id: (data.id as string) || crypto.randomUUID(),
            type: (data.type as Citation['type']) || 'other',
            identifier: (data.identifier as string) || '',
            title: data.title as string | undefined,
            authors: data.authors as string[] | undefined,
            journal: data.journal as string | undefined,
            year: data.year as number | undefined,
            url: data.url as string | undefined,
            text: (data.text as string) || '',
            verified: (data.verified as boolean) || false,
          },
          fromAgent: (data.fromAgent as string) || '',
        };

      case 'step-start':
        return {
          type: 'step-start',
          stepId: data.stepId as string,
          title: (data.title as string) || '',
          description: data.description as string | undefined,
          agentType: data.agentType as string | undefined,
        };

      case 'step-progress':
        return {
          type: 'step-progress',
          stepId: data.stepId as string,
          subStep: {
            icon:
              ((data.subStep as Record<string, unknown>)?.icon as StepIcon) ||
              'loading',
            text:
              ((data.subStep as Record<string, unknown>)?.text as string) || '',
            detail: (data.subStep as Record<string, unknown>)?.detail as
              | string
              | undefined,
          },
        };

      case 'step-complete':
        return {
          type: 'step-complete',
          stepId: data.stepId as string,
          summary: data.summary as string | undefined,
          citationsFound: data.citationsFound as number | undefined,
          // Forward the fields the doc-writer's tool-use source cards need.
          // These were silently dropped here, so the Claude-style source
          // list (with arXiv/bioRxiv "⚠ preprint" badges) could never
          // render from the live stream even though the backend emits them.
          duration: data.duration as number | undefined,
          query: data.query as string | undefined,
          sources: data.sources as ToolUseSource[] | undefined,
        };

      case 'step-error':
        return {
          type: 'step-error',
          stepId: data.stepId as string,
          error: (data.error as string) || 'Unknown step error',
          recoverable: (data.recoverable as boolean) || false,
        };

      case 'text-delta':
        // Real-time text streaming - forward directly
        return {
          type: 'text-delta',
          text: (data.text as string) || '',
          planId: data.planId as string | undefined,
        };

      case 'synthesis-start':
        return {
          type: 'synthesis-start',
          planId: data.planId as string,
          agentCount: (data.agentCount as number) || 0,
          citationCount: (data.citationCount as number) || 0,
        };

      case 'synthesis-progress':
        return {
          type: 'synthesis-progress',
          planId: data.planId as string,
          message: (data.message as string) || '',
          percentage: data.percentage as number | undefined,
        };

      case 'complete':
        return {
          type: 'complete',
          result: data.result as OrchestrationEvent extends { type: 'complete' }
            ? OrchestrationEvent['result']
            : never,
        };

      case 'error':
        return {
          type: 'error',
          error: (data.error as string) || 'Unknown error',
          recoverable: (data.recoverable as boolean) || false,
          planId: data.planId as string | undefined,
        };

      case 'cancelled':
        return {
          type: 'error',
          error: 'Orchestration was cancelled',
          recoverable: false,
          planId: data.planId as string | undefined,
        };

      default:
        console.warn(`Unknown SSE event type: ${type}`);
        return null;
    }
  }
}

/**
 * Create a singleton instance of the stream client
 */
let streamClientInstance: OrchestrationStreamClient | null = null;

export function getOrchestrationStreamClient(
  baseUrl?: string
): OrchestrationStreamClient {
  if (!streamClientInstance) {
    streamClientInstance = new OrchestrationStreamClient(baseUrl);
  }
  return streamClientInstance;
}
